import React, { useEffect, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

function KotMenuTypeMaster({ token, selectedProperty }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ kotmenutype: '', is_active: true });
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();

  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const authHeaders = (extra = {}) => ({ Authorization: `Bearer ${token}`, 'x-chain-id': chainId, 'x-property-id': propertyId, ...extra });

  useEffect(() => {
    if (!chainId || !propertyId) {
      setRows([]);
      setError('Select a property first from Hotel Config');
      return;
    }
    setError('');
    loadRows();
  }, [token, chainId, propertyId]);

  const loadRows = () => {
    if (!chainId || !propertyId) {
      setRows([]);
      setError('Select a property first from Hotel Config');
      return;
    }
    fetch('/api/v1/kot/masters/menu-types', { headers: authHeaders() })
      .then(async (res) => { const data = await res.json().catch(() => ({})); if (!res.ok) throw new Error(data.error || 'Failed to load KOT Menu Types'); return data; })
      .then(data => setRows(Array.isArray(data) ? data : []))
      .catch((err) => {
        setRows([]);
        setError(err?.message || 'Failed to load KOT Menu Types');
      });
  };

  const handleSave = async () => {
    if (!chainId || !propertyId) {
      setError('Select a property first from Hotel Config');
      return;
    }
    if (!String(form.kotmenutype || '').trim()) {
      setError('Menu Type is required');
      return;
    }

    try {
      const res = await fetch(editingId ? `/api/v1/kot/masters/menu-types/${editingId}` : '/api/v1/kot/masters/menu-types', {
        method: editingId ? 'PUT' : 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Save failed');
        return;
      }
      setError('');
      setEditingId(null);
      setForm({ kotmenutype: '', is_active: true });
      loadRows();
    } catch {
      setError('Network error');
    }
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setForm({ kotmenutype: row.kotmenutype || '', is_active: row.is_active !== false });
    setError('');
    scrollToForm();
  };

  const handleDelete = async (id) => {
    if (!chainId || !propertyId) {
      setError('Select a property first from Hotel Config');
      return;
    }
    try {
      const res = await fetch(`/api/v1/kot/masters/menu-types/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Delete failed');
        return;
      }
      setError('');
      loadRows();
    } catch {
      setError('Network error');
    }
  };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)' }}>KOT Menu Type</h2>
      {error && <div className="error-banner">{error}</div>}

      <div className="filter-bar" ref={formRef} style={editHighlightStyle(editHighlight)}>
        <h3>{editingId ? 'Edit Menu Type' : 'Add Menu Type'}</h3>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:'1rem' }}>
          <input
            placeholder="KOT Menu Type"
            value={form.kotmenutype}
            onChange={e => setForm({ ...form, kotmenutype: e.target.value })}
            style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1' }}
          />
          <label style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={e => setForm({ ...form, is_active: e.target.checked })}
            />
            Active
          </label>
        </div>
        <div style={{ marginTop:'1rem', display:'flex', gap:'0.75rem' }}>
          <button onClick={handleSave} className="btn-primary" style={{ padding:'0.75rem 1.3rem', border:'none', borderRadius:'6px', cursor:'pointer' }}>
            {editingId ? 'Update' : 'Add'}
          </button>
          {editingId && (
            <button onClick={() => { setEditingId(null); setForm({ kotmenutype: '', is_active: true }); setError(''); }} className="btn-muted" style={{ padding:'0.75rem 1.3rem', border:'none', borderRadius:'6px', cursor:'pointer' }}>
              Cancel
            </button>
          )}
        </div>
      </div>

      <h3>KOT Menu Type List</h3>
      {rows.length === 0 ? (
        <p>No KOT Menu Types found</p>
      ) : (
        <table style={{ width:'100%', borderCollapse:'collapse', background:'#fff' }}>
          <thead>
            <tr style={{ background:'var(--theme-primary-dark)', color:'#fff' }}>
              <th style={{ padding:'0.75rem', textAlign:'left' }}>ID</th>
              <th style={{ padding:'0.75rem', textAlign:'left' }}>KOT Menu Type</th>
              <th style={{ padding:'0.75rem', textAlign:'left' }}>Active</th>
              <th style={{ padding:'0.75rem', textAlign:'left' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.id} style={{ borderBottom:'1px solid #dcdde1' }}>
                <td style={{ padding:'0.75rem' }}>{row.id}</td>
                <td style={{ padding:'0.75rem' }}>{row.kotmenutype}</td>
                <td style={{ padding:'0.75rem' }}>{row.is_active ? 'Yes' : 'No'}</td>
                <td style={{ padding:'0.75rem', display:'flex', gap:'0.5rem' }}>
                  <button onClick={() => handleEdit(row)} className="btn-primary" style={{ border:'none', borderRadius:'4px', padding:'0.45rem 0.8rem', cursor:'pointer' }}>Edit</button>
                  <button onClick={() => handleDelete(row.id)} className="btn-danger" style={{ border:'none', borderRadius:'4px', padding:'0.45rem 0.8rem', cursor:'pointer' }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default KotMenuTypeMaster;
